import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { child, get, off, onValue, ref, update } from 'firebase/database';
import { useAppDataContext } from './AppDataContext';
import { useAuth } from './AuthContext';
import { database } from '../firebase/config';
import { DATA_VERSION, ERP_ROOT, STORAGE_KEY, SYNC_DEBOUNCE_MS } from '../config/constants';
import { normalizeLogList, normalizeList, normalizeCustomerList, toCustomerMap } from '../utils/normalizers';
import { isEmptyValue, stripUndefined, toPlainArray } from '../utils/object';

const SyncContext = createContext(null);

// Critical keys that must NEVER be written as empty to Firebase
const CRITICAL_KEYS = new Set(['bottle', 'sku', 'winery', 'log', 'orders', 'customer', 'users', 'suppliers']);

// All Firebase keys we listen to / sync
const LISTENER_KEYS = [
  'bottle', 'sku', 'winery', 'log', 'shipment', 'orders',
  'customer', 'packageType', 'operatingCosts', 'distributors',
  'tasks', 'memos', 'users', 'pettyCash', 'quizResults',
  'purchases', 'salesRecords', 'invoices',
  'stock_check_rotation', 'stock_adjustment_log',
  'suppliers', 'purchase_orders', 'meta',
];

function sanitizeUserRecord(value) {
  if (!value || typeof value !== 'object') return value;
  const { pin: PIN, ...safeUser } = value;
  void PIN;
  return safeUser;
}

function AuthenticatedSyncProvider({ children }) {
  const [syncStatus, setSyncStatus] = useState('connecting');
  const [firebaseConnected, setFirebaseConnected] = useState(false);

  const {
    bottles, setBottles,
    skuMap, setSkuMap,
    wineryMap, setWineryMap,
    logs, setLogs,
    shipments, setShipments,
    orders, setOrders,
    customers, setCustomers,
    packageTypes, setPackageTypes,
    operatingCosts, setOperatingCosts,
    distributors, setDistributors,
    tasks, setTasks,
    memos, setMemos,
    users, setUsers,
    pettyCash, setPettyCash,
    quizResults, setQuizResults,
    purchases, setPurchases,
    salesRecords, setSalesRecords,
    invoices, setInvoices,
    stockCheckRotation, setStockCheckRotation,
    stockAdjustmentLog, setStockAdjustmentLog,
    suppliers, setSuppliers,
    purchaseOrders, setPurchaseOrders,
    meta, setMeta,
    DEFAULT_OPERATING_COSTS,
  } = useAppDataContext();

  const listenerKeys = useRef(new Set());
  const hydrationKeys = useRef(new Set());
  const dirtyKeys = useRef(new Set());
  const dataReady = useRef(false);

  const saveTimer = useRef(null);
  const readyTimer = useRef(null);

  // Map Firebase keys → state setters (with normalization)
  const setters = useMemo(
    () => ({
      bottle: (v) => {
        if (v && typeof v === 'object' && Object.keys(v).length > 0) setBottles(v);
      },
      sku: (v) => {
        if (v && typeof v === 'object' && Object.keys(v).length > 0) setSkuMap(v);
      },
      winery: (v) => {
        if (v && typeof v === 'object' && Object.keys(v).length > 0) setWineryMap(v);
      },
      log: (v) => { const list = normalizeLogList(v); if (list.length > 0) setLogs(list); },
      shipment: (v) => { const list = normalizeList(v); if (list.length > 0) setShipments(list); },
      orders: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setOrders(arr); },
      customer: (v) => { const list = normalizeCustomerList(v); if (list.length > 0) setCustomers(list); },
      packageType: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setPackageTypes(arr); },
      operatingCosts: (v) => { const arr = toPlainArray(v, DEFAULT_OPERATING_COSTS); if (arr.length > 0) setOperatingCosts(arr); },
      distributors: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setDistributors(arr); },
      tasks: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setTasks(arr); },
      memos: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setMemos(arr); },
      users: (v) => { const arr = toPlainArray(v).map(sanitizeUserRecord); if (arr.length > 0) setUsers(arr); },
      pettyCash: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setPettyCash(arr); },
      quizResults: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setQuizResults(arr); },
      purchases: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setPurchases(arr); },
      salesRecords: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setSalesRecords(arr); },
      invoices: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setInvoices(arr); },
      stock_check_rotation: (v) => { if (v && typeof v === 'object') setStockCheckRotation(v); },
      stock_adjustment_log: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setStockAdjustmentLog(arr); },
      suppliers: (v) => {
        if (v && typeof v === 'object' && Object.keys(v).length > 0) setSuppliers(v);
      },
      purchase_orders: (v) => { const arr = toPlainArray(v); if (arr.length > 0) setPurchaseOrders(arr); },
      meta: (v) => { if (v && typeof v === 'object') setMeta(v); },
    }),
    // Setters from useState are stable — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Hydrate ALL state from a data object (Firebase or localStorage)
  const hydrateState = useCallback(
    (data) => {
      LISTENER_KEYS.forEach((key) => {
        if (data[key] !== undefined) {
          hydrationKeys.current.add(key);
          if (setters[key]) setters[key](data[key]);
        }
      });
    },
    [setters]
  );

  // 1. Initial hydration: Firebase first, localStorage fallback
  useEffect(() => {
    let cancelled = false;
    const localRaw = localStorage.getItem(STORAGE_KEY);
    const activeListenerKeys = listenerKeys.current;
    const activeHydrationKeys = hydrationKeys.current;
    const activeDirtyKeys = dirtyKeys.current;

    const hydrateLocal = () => {
      if (cancelled) return;
      if (!localRaw) return;
      try {
        const parsed = JSON.parse(localRaw);
        hydrateState(parsed);
      } catch {
        // ignore broken cache
      }
    };

    async function bootstrap() {
      try {
        const rootRef = ref(database, ERP_ROOT);
        const snapshot = await get(rootRef);
        if (cancelled) return;
        const remote = snapshot.val();

        if (remote && typeof remote === 'object' && Object.keys(remote).length > 0) {
          hydrateState(remote);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(remote));
          } catch { /* localStorage full */ }
          setFirebaseConnected(true);
          setSyncStatus('online');
        } else {
          hydrateLocal();
          setSyncStatus('online');
          setFirebaseConnected(true);
        }
      } catch {
        hydrateLocal();
        setSyncStatus('offline');
      }

      readyTimer.current = setTimeout(() => {
        if (cancelled) return;
        dataReady.current = true;
        readyTimer.current = null;
      }, 500);
    }

    void bootstrap();

    return () => {
      cancelled = true;
      activeListenerKeys.clear();
      activeHydrationKeys.clear();
      activeDirtyKeys.clear();

      if (readyTimer.current) {
        clearTimeout(readyTimer.current);
        readyTimer.current = null;
      }
      dataReady.current = false;
    };
  }, [hydrateState]);

  // 2. Real-time Firebase listeners
  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const connCb = (snapshot) => {
      const connected = snapshot.val() === true;
      setSyncStatus(connected ? 'online' : 'offline');
      setFirebaseConnected(connected);
    };
    onValue(connectedRef, connCb);

    const rootRef = ref(database, ERP_ROOT);
    const listeners = {};

    LISTENER_KEYS.forEach((key) => {
      const cb = (snapshot) => {
        const value = snapshot.val();
        if (value === undefined || value === null) return;
        listenerKeys.current.add(key);
        if (setters[key]) setters[key](value);
      };
      listeners[key] = cb;
      onValue(child(rootRef, key), cb);
    });

    return () => {
      off(connectedRef, 'value', connCb);
      LISTENER_KEYS.forEach((key) => {
        off(child(rootRef, key), 'value', listeners[key]);
      });
    };
  }, [setters]);

  // 3. Per-key dirty tracking
  const markDirty = useCallback((key) => {
    if (!dataReady.current) return;

    if (listenerKeys.current.has(key)) {
      listenerKeys.current.delete(key);
      return;
    }

    if (hydrationKeys.current.has(key)) {
      hydrationKeys.current.delete(key);
      return;
    }

    dirtyKeys.current.add(key);
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { markDirty('bottle'); }, [bottles]);
  useEffect(() => { markDirty('sku'); }, [skuMap]);
  useEffect(() => { markDirty('winery'); }, [wineryMap]);
  useEffect(() => { markDirty('log'); }, [logs]);
  useEffect(() => { markDirty('shipment'); }, [shipments]);
  useEffect(() => { markDirty('orders'); }, [orders]);
  useEffect(() => { markDirty('customer'); }, [customers]);
  useEffect(() => { markDirty('packageType'); }, [packageTypes]);
  useEffect(() => { markDirty('operatingCosts'); }, [operatingCosts]);
  useEffect(() => { markDirty('distributors'); }, [distributors]);
  useEffect(() => { markDirty('tasks'); }, [tasks]);
  useEffect(() => { markDirty('memos'); }, [memos]);
  useEffect(() => { markDirty('users'); }, [users]);
  useEffect(() => { markDirty('pettyCash'); }, [pettyCash]);
  useEffect(() => { markDirty('quizResults'); }, [quizResults]);
  useEffect(() => { markDirty('purchases'); }, [purchases]);
  useEffect(() => { markDirty('salesRecords'); }, [salesRecords]);
  useEffect(() => { markDirty('invoices'); }, [invoices]);
  useEffect(() => { markDirty('stock_check_rotation'); }, [stockCheckRotation]);
  useEffect(() => { markDirty('stock_adjustment_log'); }, [stockAdjustmentLog]);
  useEffect(() => { markDirty('suppliers'); }, [suppliers]);
  useEffect(() => { markDirty('purchase_orders'); }, [purchaseOrders]);
  useEffect(() => { markDirty('meta'); }, [meta]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // 4. Auto-save: writes ONLY dirty keys to Firebase
  useEffect(() => {
    if (!dataReady.current) return;
    if (dirtyKeys.current.size === 0) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      const keysToSave = new Set(dirtyKeys.current);
      dirtyKeys.current.clear();

      if (keysToSave.size === 0) return;

      const allData = {
        meta: { ...meta, dataVersion: DATA_VERSION, updatedAt: new Date().toISOString().slice(0, 10) },
        bottle: bottles,
        sku: skuMap,
        winery: wineryMap,
        customer: toCustomerMap(customers),
        packageType: packageTypes,
        log: logs,
        shipment: shipments,
        orders,
        operatingCosts,
        distributors,
        tasks,
        memos,
        users,
        pettyCash,
        quizResults,
        purchases,
        salesRecords,
        invoices,
        stock_check_rotation: stockCheckRotation,
        stock_adjustment_log: stockAdjustmentLog,
        suppliers,
        purchase_orders: purchaseOrders,
      };

      // Save ALL data to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));
      } catch {
        // localStorage full
      }

      // Save ONLY dirty keys to Firebase
      if (!firebaseConnected) return;

      const partialUpdate = { meta: allData.meta };

      keysToSave.forEach((key) => {
        const fbKey = key;
        const value = allData[fbKey];
        if (value === undefined) return;
        if (CRITICAL_KEYS.has(key) && isEmptyValue(value)) return;
        partialUpdate[fbKey] = value;
      });

      if (Object.keys(partialUpdate).length <= 1) return;

      const rootRef = ref(database, ERP_ROOT);
      update(rootRef, stripUndefined(partialUpdate)).catch(() => {
        keysToSave.forEach((k) => dirtyKeys.current.add(k));
        setSyncStatus('offline');
      });
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [
    bottles, skuMap, wineryMap, customers, packageTypes, logs, shipments,
    orders, operatingCosts, distributors, tasks, memos, users, pettyCash,
    quizResults, purchases, salesRecords, invoices, stockCheckRotation,
    stockAdjustmentLog, suppliers, purchaseOrders, meta, firebaseConnected,
  ]);

  const value = useMemo(() => ({ syncStatus, firebaseConnected }), [syncStatus, firebaseConnected]);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function SyncProvider({ children }) {
  const { user, isLoading: authLoading } = useAuth();

  const value = useMemo(
    () => ({
      syncStatus: authLoading ? 'connecting' : 'offline',
      firebaseConnected: false,
    }),
    [authLoading]
  );

  if (authLoading || !user?.firebaseUid) {
    return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
  }

  return <AuthenticatedSyncProvider key={user.firebaseUid}>{children}</AuthenticatedSyncProvider>;
}

export function useSync() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}
